import { Flex, Typography } from "antd";
import { ChangeEvent, FC, useCallback, useMemo } from "react";
import { Point, TaskPaidShelfSpace, TaskRoute } from "tm-shared/types";

import { GalleryUploader } from "@/domains/media";
import { usePointDistance } from "@/domains/point";
import { useCompleteTask } from "@/domains/tasks/hooks";
import { SVScanButton } from "@/features/SupervisorScan";
import { makeCommand, useAsyncAction, useMakeCommand } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useTaskPayloadSelector,
} from "@/hooks/redux";
import { TaskFinishButton } from "@/pages/WorkPlan/Tasks/components/TaskFinishButton";
import { Loading } from "@/shared/feedback/Loading";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import {
    mutatePaidShelfSpacePhotos,
    patchPaidShelfSpaceEntity,
} from "@/store/slices/progress";
import { buildPhotoCaptions } from "@/utils/photo/captions";

import { StyledBlock, StyledContainer, StyledTextArea } from "./Styled";

interface PaidShelfSpacePageProps {
    point: Point;
    task: TaskPaidShelfSpace;
}

export const PaidShelfSpacePage: FC<PaidShelfSpacePageProps> = ({
    point,
    task,
}) => {
    const dispatch = useAppDispatch();
    const pointId = point.id;
    const taskId = task.id;

    const { firstName, lastName } = useAppSelector((s) => s.user);
    const { distance } = usePointDistance({
        pointGeo: point.market.building.geo,
    });

    const payload = useTaskPayloadSelector(TaskRoute.PaidShelfSpace, taskId);

    const captions = useMemo(
        () =>
            buildPhotoCaptions({
                pointName: point.name,
                taskName: task.name,
                userFullName: `${firstName} ${lastName}`,
                distance,
            }),
        [distance, firstName, lastName, point.name, task.name],
    );

    const isFinishButtonVisible = useMemo(() => {
        return Boolean(payload?.comment || payload?.photos?.length);
    }, [payload]);

    const onChangeComment = useCallback(
        (evt: ChangeEvent<HTMLTextAreaElement>) => {
            dispatch(
                patchPaidShelfSpaceEntity({
                    taskId,
                    patch: { comment: evt.target.value },
                }),
            );
        },
        [dispatch, taskId],
    );

    const onUploadPhoto = useCallback(
        (photo: string) => {
            dispatch(
                mutatePaidShelfSpacePhotos({
                    taskId,
                    option: "add",
                    photo,
                }),
            );
        },
        [dispatch, taskId],
    );

    const onDeletePhoto = useCallback(
        (photoId: string) => {
            dispatch(
                mutatePaidShelfSpacePhotos({
                    taskId,
                    option: "remove",
                    photoId,
                }),
            );
        },
        [dispatch, taskId],
    );

    const completeTask = useCompleteTask({ targetId: pointId, taskId });
    const { makeTaskCompleteCommands, makeUploadPhotoCommands } =
        useMakeCommand();

    const [handleComplete, completeLoading] = useAsyncAction(async () => {
        if (!payload) return;

        const photoIds = payload?.photos ?? [];
        const hasPhoto = photoIds.length > 0;

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: { taskId, hasPhoto, payload },
        });

        const commands = [
            ...makeUploadPhotoCommands(photoIds),
            ...taskCompleteCommands,
            makeCommand({
                type: "updatePhotoStorage",
                title: "Синхронізація сховища",
                params: photoIds,
                options: {
                    stopOnError: false,
                },
            }),
        ];

        completeTask({ commands });
    });

    return (
        <>
            <Header
                back
                title={task.name}
                rightActions={
                    <SVScanButton pointId={pointId} taskId={taskId} />
                }
            />

            <MainContainer>
                <StyledContainer>
                    <Flex vertical gap="16px">
                        {task.context.contracts.map((contract) => {
                            return (
                                <StyledBlock key={contract.id}>
                                    <Typography.Title
                                        level={5}
                                        style={{ margin: 0 }}
                                    >
                                        {contract.name}
                                    </Typography.Title>
                                    {contract.description && (
                                        <Typography.Paragraph
                                            style={{ margin: 0, opacity: 0.8 }}
                                        >
                                            {contract.description}
                                        </Typography.Paragraph>
                                    )}
                                </StyledBlock>
                            );
                        })}

                        <StyledBlock>
                            <Typography.Title level={5} style={{ margin: 0 }}>
                                Звіт по задачі
                            </Typography.Title>

                            <StyledTextArea
                                placeholder="Введіть коментар"
                                rows={4}
                                value={payload?.comment ?? ""}
                                minLength={5}
                                onChange={onChangeComment}
                            />

                            <GalleryUploader
                                previewTitle={task.name}
                                onUpload={onUploadPhoto}
                                onDelete={onDeletePhoto}
                                pointId={pointId}
                                savedPhotos={payload?.photos ?? []}
                                captionLines={captions}
                            />
                        </StyledBlock>
                    </Flex>

                    <TaskFinishButton
                        onClick={handleComplete}
                        visible={isFinishButtonVisible}
                        loading={completeLoading}
                        padding="0px"
                    />
                </StyledContainer>
            </MainContainer>

            {completeLoading && <Loading title="Завершення задачі" />}
        </>
    );
};

export default PaidShelfSpacePage;
